import React, { useState, useContext, useEffect } from "react";
import {
  FaSearch,
  FaUser,
  FaEnvelope,
  FaCalendar,
  FaComment,
  FaBan,
  FaCheck,
  FaEllipsisV,
} from "react-icons/fa";
import DataTable from "react-data-table-component";
import { AppContext } from "../Context/Provider";
import CommentActionModal from "./CommentActionModal";

const CommentsTab = ({ hideActions = false }) => {
  const [filterText, setFilterText] = useState("");
  const [selectedComment, setSelectedComment] = useState(null);
  const [showActionModal, setShowActionModal] = useState(false);
  const { FetchCommentsData, CommentsData, UpdateCommentStatus } =
    useContext(AppContext);

  // Fetch comments data on component mount
  useEffect(() => {
    FetchCommentsData();
  }, []);

  // Comments data from API
  const commentsData = CommentsData || [];

  // Filter data based on search text
  const filteredData = commentsData.filter(
    (item) =>
      (item.name || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.email || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.comments || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.status || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (item.date || "").toLowerCase().includes(filterText.toLowerCase())
  );

  // Clear search function
  const clearSearch = () => {
    setFilterText("");
  };

  // Handle comment row click
  const handleCommentClick = (row) => {
    setSelectedComment(row);
    setShowActionModal(true);
  };

  // Handle status update from modal
  const handleStatusUpdate = (status) => {
    if (!selectedComment) return;
    UpdateCommentStatus(selectedComment._id, status);
    setShowActionModal(false);
    setSelectedComment(null);
  };

  // Close modal
  const closeModal = () => {
    setShowActionModal(false);
    setSelectedComment(null);
  };

  // Comments table columns
  const commentsColumns = [
    {
      name: "NAME",
      selector: (row) => row.name || "N/A",
      sortable: true,
      grow: 1,
      minWidth: "150px",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <FaUser className="text-gray-500 text-sm" />
          <span className="font-medium">{row.name || "N/A"}</span>
        </div>
      ),
    },
    {
      name: "EMAIL",
      selector: (row) => row.email || "N/A",
      sortable: true,
      grow: 2,
      minWidth: "200px",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <FaEnvelope className="text-gray-500 text-sm" />
          <span className="text-blue-600 hover:text-blue-800">
            {row.email || "N/A"}
          </span>
        </div>
      ),
    },
    {
      name: "COMMENT",
      selector: (row) => row.comments || "N/A",
      sortable: true,
      grow: 3,
      minWidth: "300px",
      cell: (row) => (
        <div className="flex items-start gap-2">
          <FaComment className="text-gray-500 text-sm mt-1 flex-shrink-0" />
          <span className="text-sm leading-relaxed">
            {row.comments || "N/A"}
          </span>
        </div>
      ),
    },
    {
      name: "STATUS",
      selector: (row) => row.status || "N/A",
      sortable: true,
      grow: 1,
      minWidth: "120px",
      cell: (row) => {
        const status = (row.status || "").toLowerCase();
        let icon, bgColor, textColor;

        switch (status) {
          case "blocked":
            icon = <FaBan className="text-red-500 text-sm" />;
            bgColor = "bg-red-100 text-red-800";
            break;
          case "deleted":
            icon = <FaBan className="text-gray-500 text-sm" />;
            bgColor = "bg-gray-100 text-gray-800";
            break;
          case "approved":
            icon = <FaCheck className="text-green-500 text-sm" />;
            bgColor = "bg-green-100 text-green-800";
            break;
          case "pending":
          default:
            icon = <FaCalendar className="text-yellow-500 text-sm" />;
            bgColor = "bg-yellow-100 text-yellow-800";
            break;
        }

        return (
          <div className="flex items-center gap-2">
            {icon}
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${bgColor}`}
            >
              {row.status || "N/A"}
            </span>
          </div>
        );
      },
    },
    {
      name: "DATE",
      selector: (row) => row.date || row.createdAt || "N/A",
      sortable: true,
      grow: 1,
      minWidth: "150px",
      cell: (row) => (
        <div className="flex items-center gap-2">
          <FaCalendar className="text-gray-500 text-sm" />
          <span className="text-sm">
            {row.date || row.createdAt
              ? new Date(row.date || row.createdAt).toLocaleString()
              : "N/A"}
          </span>
        </div>
      ),
    },
    // Only show ACTIONS column if hideActions is false
    ...(hideActions
      ? []
      : [
          {
            name: "ACTIONS",
            selector: (row) => row._id,
            sortable: false,
            grow: 0,
            width: "120px",
            cell: (row) => {
              return (
                <div className="relative">
                  <button
                    onClick={() => handleCommentClick(row)}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <FaEllipsisV className="text-sm" />
                  </button>
                </div>
              );
            },
          },
        ]),
  ];

  // Custom styles for DataTable
  const customStyles = {
    table: {
      style: {
        width: "100%",
        minWidth: "100%",
        tableLayout: "auto",
      },
    },
    rows: {
      style: {
        minHeight: "72px",
        cursor: "pointer",
        "&:hover": {
          backgroundColor: "#f3f4f6",
        },
      },
    },
    headCells: {
      style: {
        backgroundColor: "#f9fafb",
        borderBottom: "1px solid #e5e7eb",
        padding: "12px 16px",
        fontSize: "0.75rem",
        fontWeight: "600",
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: "#374151",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: "100%",
      },
    },
    cells: {
      style: {
        padding: "16px 16px",
        fontSize: "0.875rem",
        color: "#374151",
      },
    },
  };

  return (
    <div className="w-full p-6">
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">
          Comments Management
        </h1>
        <p className="text-gray-600">Manage and moderate user comments</p>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <input
            type="text"
            placeholder="Search comments..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
          {filterText ? (
            <button
              onClick={clearSearch}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          ) : (
            <FaSearch className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          )}
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-x-auto border border-gray-100 w-full">
        {CommentsData && CommentsData.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-gray-500">No comments data available</p>
          </div>
        ) : CommentsData ? (
          <DataTable
            columns={commentsColumns}
            data={filteredData}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 25, 50]}
            customStyles={customStyles}
            defaultSortField="date"
            defaultSortAsc={false}
            subHeader
            subHeaderComponent={
              <div className="text-sm text-gray-600">
                {filterText &&
                  `Found ${filteredData.length} results for "${filterText}"`}
              </div>
            }
            responsive={true}
            fixedHeader
            noTableHead={false}
            highlightOnHover
          />
        ) : (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
            <p className="text-gray-500">Loading comments data...</p>
          </div>
        )}
      </div>

      {/* Action Modal */}
      {showActionModal && selectedComment && (
        <CommentActionModal
          isOpen={showActionModal}
          comment={selectedComment}
          onClose={closeModal}
          onStatusUpdate={handleStatusUpdate}
        />
      )}
    </div>
  );
};

export default CommentsTab;
